import { Rule } from 'antd/lib/form';
import FormSelect from 'components/Select/FormSelect';
import { ISelectOption } from 'components/Select/types';
import Text from 'components/Text/Text';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { IItemForGetShortInfoWithStatus, ITotalPrice, EntityStatus } from 'types';
import { SHOW_ARCHIVED_RESTAURANTS } from 'appConstants';
import { Checkbox } from 'antd';
import SearchField from 'components/SearchField/SearchField';
import { useTranslation } from 'react-i18next';
import style from './MultipleSelect.module.scss';

interface IMultipleSelect {
	data: IItemForGetShortInfoWithStatus[];
	isLoading: boolean;
	refetch: () => void;
	responseError: boolean;
	setRestaurantsForPrice: (value: string[]) => void;
	removePrice: (value: string[]) => void;
	selectedCustomer: Nullable<string>;
	restaurantsForPrice: string[];
	totalPrices: ITotalPrice[];
}

const MultipleSelect: React.FC<IMultipleSelect> = ({
	data,
	isLoading,
	refetch,
	responseError,
	selectedCustomer,
	restaurantsForPrice,
	totalPrices,
	setRestaurantsForPrice,
	removePrice,
}) => {
	const [searchString, setSearchString] = useState<string>('');
	const [showArchived, setShowArchived] = useState<boolean>(
		localStorage.getItem(SHOW_ARCHIVED_RESTAURANTS) === 'true',
	);
	const { t } = useTranslation('error');

	const visibleData = useMemo(() => {
		const items = showArchived
			? data
			: data.filter((el) => el.status === EntityStatus.Active || restaurantsForPrice.includes(el.id));

		return items.map((el) => ({
			...el,
			additionalContent: el.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
		}));
	}, [data, showArchived, restaurantsForPrice]);

	const filteredData = useMemo<ISelectOption[]>(() => {
		return searchString
			? visibleData.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase()))
			: visibleData;
	}, [searchString, visibleData]);

	const validationRules = useMemo<Rule[]>(
		() => [
			() => ({
				validator() {
					const IDsTotalPrices = totalPrices.map((el) => el.restaurantId);
					const isAllRestaurantsHavePrice = restaurantsForPrice.every((el) => IDsTotalPrices.includes(el));
					if (!isAllRestaurantsHavePrice) {
						return Promise.reject(new Error(t('enterPrice')));
					}
					return Promise.resolve();
				},
			}),
		],
		[totalPrices, restaurantsForPrice, t],
	);

	// ----- where roles are used, the selection of all is prohibited ----- //
	const handleSelectAllData = useCallback(() => {
		if (visibleData.length === restaurantsForPrice.length) {
			setRestaurantsForPrice([]);
			const selectedRestaurantIds = visibleData.map((el) => el.id);
			removePrice(selectedRestaurantIds);
		} else {
			setRestaurantsForPrice(visibleData.map((el) => el.id));
		}
	}, [visibleData, restaurantsForPrice, setRestaurantsForPrice, removePrice]);
	// ----- where roles are used, the selection of all is prohibited ----- //

	const toggleShowArchived = useCallback(() => {
		localStorage.setItem(SHOW_ARCHIVED_RESTAURANTS, JSON.stringify(!showArchived));
		setShowArchived((prevValue) => !prevValue);
	}, [showArchived]);

	useEffect(() => {
		setSearchString('');
	}, [selectedCustomer]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => {
			return (
				<>
					<div className={style.selectMenuHeader}>
						<Checkbox
							className={style.checkBox}
							checked={!!(visibleData.length && visibleData.length === restaurantsForPrice.length)}
							disabled={!visibleData.length}
							onChange={handleSelectAllData}>
							<Text
								translationNameSpace="form"
								translationKey="selectAllRestaurantsLabel"
								asFragment={true}
							/>
						</Checkbox>
						<Checkbox className={style.checkBox} checked={showArchived} onChange={toggleShowArchived}>
							<Text translationNameSpace="modals" translationKey="showArchived" asFragment={true} />
						</Checkbox>
						<SearchField
							onChangeSelectSearchString={setSearchString}
							searchString={searchString}
							className={style.searchField}
						/>
					</div>
					{menu}
				</>
			);
		},
		[
			visibleData,
			restaurantsForPrice,
			handleSelectAllData,
			setSearchString,
			searchString,
			showArchived,
			toggleShowArchived,
		],
	);

	return (
		<FormSelect
			name="restaurantsForPrice"
			label="restaurantsLabel"
			options={filteredData}
			fetchError={responseError}
			loading={isLoading}
			onRefetch={refetch}
			disabled={!selectedCustomer || isLoading}
			onChange={setRestaurantsForPrice}
			onDeselect={removePrice}
			counterValue={restaurantsForPrice.length}
			validationRules={validationRules}
			dropdownRender={renderSelectMenu}
			mode="multiple"
		/>
	);
};

export default React.memo(MultipleSelect);
